<template>
  <div class="rf-detail-content-wrapper mt-4">
    <div class="mb-6 flex min-h-12 items-center justify-between gap-16">
      <h1 class="text-2xl font-semibold uppercase">Audit Logs</h1>
    </div>

    <!-- Guide -->
    <RfAccordionCard class="mb-4" title="How to use Audit Logs">
      <div class="flex flex-col gap-4 text-sm">
        <div>
          <h3 class="mb-2 font-semibold">What are Audit Logs?</h3>
          <p class="text-gray-700">
            Audit logs track all changes made to entities in the system. Each record shows what was
            changed, who changed it, and when. This is primarily for internal debugging and
            investigating issues.
          </p>
        </div>

        <div>
          <h3 class="mb-2 font-semibold">Available Filters</h3>
          <ul class="list-inside list-disc space-y-1 text-gray-700">
            <li>
              <b>Entity Type:</b> Filter by the type of entity being audited (App, Path, Segment,
              Action, Display, etc.). Select <b>App</b> to see changes made to the app record
              itself, rather than to entities belonging to the app.
            </li>
            <li>
              <b>Entity ID:</b> Filter by the specific ID of an entity (e.g., show only changes to
              Path ID 123)
            </li>
            <li>
              <b>Associated Type:</b> Filter by the parent entity type (typically App or Path). Most
              audits are grouped by their parent association; changes to the app record itself have
              no parent — the App column shows the app directly.
            </li>
            <li>
              <b>Associated ID:</b> Filter by the specific parent entity ID (e.g., show audits for a
              specific App or Path)
            </li>
            <li><b>User ID:</b> Filter by the user who made the changes</li>
            <li><b>Date Range:</b> Filter by when the changes occurred</li>
            <li>
              <b>Note:</b> When clicking "View all" from a History section, filters are set to show
              all audits associated with that entity, including nested resources (e.g., for a Path,
              this includes Actions and Displays)
            </li>
          </ul>
        </div>

        <div>
          <h3 class="mb-2 font-semibold">Understanding the Table</h3>
          <ul class="list-inside list-disc space-y-1 text-gray-700">
            <li><b>Timestamp:</b> When the change occurred</li>
            <li><b>Entity Type & Name:</b> What was changed</li>
            <li><b>Entity ID:</b> The database ID of the entity</li>
            <li><b>App:</b> Which app the entity belongs to</li>
            <li><b>Action:</b> create, update, or destroy</li>
            <li><b>User:</b> Who made the change</li>
            <li><b>Changes:</b> How many fields were modified</li>
            <li>
              <b>Details:</b> Click "View" to open a modal with before/after values and syntax
              highlighting
            </li>
          </ul>
        </div>

        <div>
          <h3 class="mb-2 font-semibold">Tips</h3>
          <ul class="list-inside list-disc space-y-1 text-gray-700">
            <li>
              Use the "View all" links from the Dashboard, Prompt, or Segment pages to jump here
              with filters pre-applied
            </li>
            <li>
              The before/after comparison shows exactly what changed, useful for debugging
              unexpected behavior
            </li>
            <li>Request UUID can help correlate audit logs with application logs</li>
            <li>IP Address shows where the change originated from</li>
          </ul>
        </div>
      </div>
    </RfAccordionCard>

    <!-- Filters -->
    <RfBaseCard class="mb-4">
      <div class="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        <!-- Entity Type Filter -->
        <RfSelect
          v-model="filters.auditableType"
          :items="auditsStore.availableTypes"
          title="Entity Type"
          placeholder="Select entity type"
          variant="outlined"
          class="[&_.rf-input--wrapper]:min-w-0"
          @update:modelValue="handleFilterChange"
        />

        <!-- Entity ID Filter -->
        <RfInput
          v-model="filters.auditableId"
          title="Entity ID"
          placeholder="Enter entity ID"
          variant="outlined"
          class="[&_.rf-input--wrapper]:min-w-0"
          @update:modelValue="debouncedFilterChange"
        />

        <!-- Associated Type Filter -->
        <RfSelect
          v-model="filters.associatedType"
          :items="auditsStore.availableTypes"
          title="Associated Type"
          placeholder="Select associated type"
          variant="outlined"
          class="[&_.rf-input--wrapper]:min-w-0"
          @update:modelValue="handleFilterChange"
        />

        <!-- Associated ID Filter -->
        <RfInput
          v-model="filters.associatedId"
          title="Associated ID"
          placeholder="Enter associated ID"
          variant="outlined"
          class="[&_.rf-input--wrapper]:min-w-0"
          @update:modelValue="debouncedFilterChange"
        />

        <!-- User Filter -->
        <RfInput
          v-model="filters.userId"
          title="User ID"
          placeholder="Enter user ID"
          variant="outlined"
          class="[&_.rf-input--wrapper]:min-w-0"
          @update:modelValue="debouncedFilterChange"
        />

        <!-- Date Range -->
        <RfInput
          v-model="filters.startDate"
          title="Start Date"
          type="date"
          variant="outlined"
          class="[&_.rf-input--wrapper]:min-w-0"
          @update:modelValue="handleFilterChange"
        />

        <RfInput
          v-model="filters.endDate"
          title="End Date"
          type="date"
          variant="outlined"
          class="[&_.rf-input--wrapper]:min-w-0"
          @update:modelValue="handleFilterChange"
        />

        <!-- Clear Filters -->
        <div class="flex items-end">
          <RfButtonNew v-if="hasActiveFilters" type="secondary" @click="clearFilters">
            Clear Filters
          </RfButtonNew>
        </div>
      </div>

      <!-- Results count -->
      <div class="mt-4 text-sm text-gray-600">
        Total: {{ formattedTotalCount }} {{ pluralizedAudits }}
      </div>
    </RfBaseCard>

    <!-- Table -->
    <RfTable
      :tableData="auditsStore.table"
      :loading="auditsStore.loading"
      :rowSkeleton="RfAuditRowSkeleton"
      type="audits"
      @loadMore="loadMore"
    >
      <template #thead>
        <tr class="border-beige-20 border-b-2">
          <th class="whitespace-nowrap">
            <RfSortButton
              :filtered="sort.type === 'created_at' ? sort.value : null"
              class="h-full w-full"
              @click="value => handleSortChange({ type: 'created_at', value })"
            >
              Timestamp
            </RfSortButton>
          </th>
          <th>
            <RfSortButton
              :filtered="sort.type === 'auditable_type' ? sort.value : null"
              class="h-full w-full"
              @click="value => handleSortChange({ type: 'auditable_type', value })"
            >
              Entity Type
            </RfSortButton>
          </th>
          <th>Entity Name</th>
          <th>Entity ID</th>
          <th>App</th>
          <th>
            <RfSortButton
              :filtered="sort.type === 'action' ? sort.value : null"
              class="h-full w-full"
              @click="value => handleSortChange({ type: 'action', value })"
            >
              Action
            </RfSortButton>
          </th>
          <th>
            <RfSortButton
              :filtered="sort.type === 'user_id' ? sort.value : null"
              class="h-full w-full"
              @click="value => handleSortChange({ type: 'user_id', value })"
            >
              User
            </RfSortButton>
          </th>
          <th>Changes</th>
          <th>Details</th>
        </tr>
      </template>
      <template #tbody>
        <RfAuditRow
          v-for="audit in auditsStore.audits"
          :key="audit.id"
          :audit="audit"
          @view="openAuditModal"
        />
      </template>
    </RfTable>

    <RfAuditDiffModal ref="auditModalRef" :audit="viewAudit || {}" />
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from "vue";
import { useRoute } from "vue-router";
import { useAuditsStore } from "@/pinia/auditsStore";
import { useTableSort } from "@/utils/composables/useTableSort";
import RfTable from "@/blocks/RfTable/RfTable.vue";
import RfAuditRow from "@/blocks/RfAudits/RfAuditRow.vue";
import RfAuditRowSkeleton from "@/blocks/RfAudits/RfAuditRowSkeleton.vue";
import RfAuditDiffModal from "@/blocks/RfAudits/RfAuditDiffModal.vue";
import RfAccordionCard from "@/components/cards/RfAccordionCard.vue";
import RfBaseCard from "@/components/cards/RfBaseCard.vue";
import RfSortButton from "@/components/buttons/RfSortButton.vue";
import RfButtonNew from "@/components/buttons/RfButtonNew.vue";
import RfSelect from "@/components/inputs/RfSelect.vue";
import RfInput from "@/components/inputs/RfInput.vue";
import { debounce } from "@/utils/debounce";
import { toLocaleNumberString } from "@/utils/stringHelpers";
import StringUtils from "@/utils/StringUtils";

const route = useRoute();
const auditsStore = useAuditsStore();
const sort = useTableSort(["created_at", "auditable_type", "action", "user_id"], {
  type: "created_at",
  value: "desc",
});

const viewAudit = ref(null);
const auditModalRef = ref();

const openAuditModal = audit => {
  viewAudit.value = audit;
  auditModalRef.value?.open();
};

const filters = reactive({
  auditableType: route.query.auditable_type || null,
  auditableId: route.query.auditable_id || null,
  associatedType: route.query.associated_type || null,
  associatedId: route.query.associated_id || null,
  userId: route.query.user_id || null,
  startDate: route.query.start_date || null,
  endDate: route.query.end_date || null,
});

const hasActiveFilters = computed(() => {
  return Object.values(filters).some(v => v !== null && v !== "");
});

const formattedTotalCount = computed(() => {
  return toLocaleNumberString(auditsStore.table.totalCount);
});

const pluralizedAudits = computed(() => {
  return StringUtils.pluralize("audit", auditsStore.table.totalCount);
});

const fetchAudits = async (next = false) => {
  if (!next) {
    auditsStore.resetPage();
  }

  const params = {};
  if (filters.auditableType) params.auditable_type = filters.auditableType;
  if (filters.auditableId) params.auditable_id = filters.auditableId;
  if (filters.associatedType) params.associated_type = filters.associatedType;
  if (filters.associatedId) params.associated_id = filters.associatedId;
  if (filters.userId) params.user_id = filters.userId;
  if (filters.startDate) params.start_date = filters.startDate;
  if (filters.endDate) params.end_date = filters.endDate;
  if (sort.value.string) params.order = sort.value.string;

  await auditsStore.getAudits(params, next);
};

const handleSortChange = value => {
  sort.value = value;
  fetchAudits(false);
};

const handleFilterChange = () => {
  fetchAudits(false);
};

const debouncedFilterChange = debounce(() => {
  fetchAudits(false);
}, 500);

const loadMore = () => {
  fetchAudits(true);
};

const clearFilters = () => {
  filters.auditableType = null;
  filters.auditableId = null;
  filters.associatedType = null;
  filters.associatedId = null;
  filters.userId = null;
  filters.startDate = null;
  filters.endDate = null;
  fetchAudits(false);
};

onMounted(async () => {
  await auditsStore.getFilters();
  await fetchAudits(false);
});
</script>
